/**
 * Clipboard View - Clipboard manager with global chat input
 * This component provides the clipboard management interface with an accessible chat input
 */
import { useState } from 'react';
import { useChatStore } from '../stores/chatStore';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { config, AI_DEFAULTS } from '../config';
import ClipboardManager from './ClipboardManager';
import ImageUploadButton from './ImageUploadButton';
import ImagePreview from './ImagePreview';

interface ClipboardViewProps {
  onSwitchToChat: () => void;
}

export default function ClipboardView({ onSwitchToChat }: ClipboardViewProps) {
  const { currentConversation, addMessageToStore, createConversation } = useChatStore();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);

  const sendMessage = async (messageContent: string) => {
    // Create new conversation if none exists
    let conversation = useChatStore.getState().currentConversation;

    if (!conversation) {
      try {
        conversation = await createConversation('New Chat');
      } catch (error) {
        console.error('Failed to create conversation:', error);
        return;
      }
    }

    // Switch to chat view to see the conversation
    onSwitchToChat();

    setIsLoading(true);

    try {
      // Add user message to UI optimistically
      const userMessage: any = {
        id: crypto.randomUUID(),
        conversation_id: conversation.id,
        role: 'user' as const,
        content: messageContent,
        created_at: new Date().toISOString()
      };
      addMessageToStore(userMessage);

      const token = localStorage.getItem('access_token');

      // Create placeholder message for streaming content
      const streamingMessageId = crypto.randomUUID();
      const streamingMessage: any = {
        id: streamingMessageId,
        conversation_id: conversation.id,
        role: 'assistant' as const,
        content: '',
        created_at: new Date().toISOString()
      };
      addMessageToStore(streamingMessage);

      // Use streaming endpoint
      const formData = new FormData();
      formData.append('message', messageContent);
      formData.append('conversation_id', conversation.id);
      formData.append('max_tokens', String(AI_DEFAULTS.MAX_TOKENS));
      formData.append('temperature', String(AI_DEFAULTS.TEMPERATURE));

      const response = await authenticatedFetch(`${config.apiBaseUrl}/api/ai/chat/stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const status = response.status;
        if (status === 401) {
          throw new Error('Your session has expired. Please log in again.');
        } else if (status === 429) {
          throw new Error('You have reached your AI usage limit. Please try again later.');
        } else if (status >= 500) {
          throw new Error('The AI service is temporarily unavailable. Please try again in a moment.');
        }
        throw new Error(`Streaming request failed (${status})`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';
      let incompleteLineBuffer = ''; // Buffer for incomplete lines across chunk boundaries

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          // Prepend any incomplete line from previous chunk
          const fullChunk = incompleteLineBuffer + chunk;
          const lines = fullChunk.split('\n');

          // Last line might be incomplete (no trailing \n), save it for next iteration
          incompleteLineBuffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              const trimmedData = data.trim(); // Only for comparison checks

              if (trimmedData === '[DONE]') {
                break;
              } else if (trimmedData.startsWith('{')) {
                // Error message
                const error = JSON.parse(trimmedData);
                throw new Error(error.error);
              } else if (trimmedData) {
                // Append text chunk with preserved whitespace
                accumulatedContent += data;

                // Update the streaming message
                const updatedMessage = {
                  ...streamingMessage,
                  content: accumulatedContent
                };
                addMessageToStore(updatedMessage);
              }
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Error:', error);

      // Show error message to user
      const errorMessage: any = {
        id: crypto.randomUUID(),
        conversation_id: conversation.id,
        role: 'assistant' as const,
        content: `Error: ${error.message || 'Failed to get AI response. Please check your API keys in settings.'}`,
        created_at: new Date().toISOString(),
      };
      addMessageToStore(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userInput = input;
    setInput('');
    setSelectedImages([]);

    await sendMessage(userInput);
  };

  const handleImageRemove = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    const imageFiles: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Check if the item is an image
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }

    // Add pasted images to selected images
    if (imageFiles.length > 0) {
      setSelectedImages(prev => [...prev, ...imageFiles]);
      e.preventDefault(); // Prevent default paste behavior for images
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Clipboard Manager */}
      <div className="flex-1 overflow-y-auto">
        <ClipboardManager />
      </div>

      {/* Global Chat Input */}
      <div className="border-t border-dark-border p-2 sm:p-3 md:p-4 bg-dark-bg">
        <form onSubmit={handleSubmit} className="max-w-5xl mx-auto text-sm sm:text-base">
          {/* Image Previews */}
          {selectedImages.length > 0 && (
            <div className="mb-2">
              <ImagePreview files={selectedImages} onRemove={handleImageRemove} />
            </div>
          )}

          <div className="flex gap-3 bg-dark-card border border-dark-border rounded-xl p-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder="Start a new conversation from clipboard view..."
              className="flex-1 bg-transparent px-2 py-2 text-dark-text placeholder-dark-text-muted focus:outline-none resize-none"
              rows={1}
              disabled={isLoading}
            />
            <div className="flex items-end gap-2">
              <ImageUploadButton
                onImagesSelected={(files) => setSelectedImages(prev => [...prev, ...files])}
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="p-2 bg-gradient-to-r from-accent-blue to-accent-orange text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
